import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'

import {
  Checkbox as CheckboxComponent,
  palette,
} from '@centivo/react-components'
import { noop } from 'lodash'

import { Slider } from 'src/refactor/blocks/Slider'
import { useToggle } from 'src/hooks'
import { defaultState } from 'src/context/results/initialState'
import { useMediaContext } from 'src/context/responsive'
import { isBrowser } from 'src/refactor/helpers/is-browser'

import styled from 'styled-components'
import { useGlobalState } from 'src/context/global'
import { IEnhancedFilterProps } from '../helpers/types'
import {
  FilterContent,
  GenderButton,
  GenderSelectorContainer,
  DistanceSelectorContainer,
  CheckboxContainer,
  SpecialtyContainer,
  Container,
  Title,
  ApplyFilterButton,
  Header,
  MaxMiles,
  GenericButton,
  ResetButtonContainer,
} from '../styled/EnhancedFilter.styled'
import '../styles/enhanced-filter.scss'

const Checkbox = styled(CheckboxComponent)`
  align-items: flex-start;

  > span {
    margin-top: 2px;
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            border: `1px solid ${palette.brand.lightGray}`,
          }
        default:
          return {}
      }
    }}
  }
`

const EnhancedFilter: React.FC<IEnhancedFilterProps> = ({
  initialValues,
  onSubmit = noop,
  displayType,
  onRequestClose,
  specialtyOptions = [],
  shouldShowSpecialtyFilters,
  shouldShowVirtualCareAvailable = true,
}) => {
  const { isMobileView } = useMediaContext()
  const [distance, setDistance] = useState(initialValues.distance)
  const [gender, setGender] = useState(initialValues.gender)
  const [
    acceptingNewPatients,
    toggleAcceptingNewPatients,
    setAcceptingNewPatients,
  ] = useToggle(initialValues.newPatients)
  const [withVirtualCare, toggleWithVirtualCare, setWithVirtualCare] =
    useToggle(initialValues.withVirtualCare)
  const [specialtyFilters, setSpecialtyFilters] = useState<string[]>(
    initialValues.specialtyFilters
  )
  const [{ appConfig }] = useGlobalState()
  const isSafari = isBrowser('safari')

  const isSliceTheme = appConfig.template === 'template-4'

  useEffect(() => onRequestClose, [onRequestClose])

  const handleDistanceChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDistance(parseInt(e.currentTarget.value, 10))
  }

  const handleGenderChange = (newGender: 'unknown' | 'm' | 'f') => {
    setGender(newGender)
  }
  const onClickAnyGender = () => handleGenderChange('unknown')
  const onClickMale = () => handleGenderChange('m')
  const onClickFemale = () => handleGenderChange('f')

  const onChangeSpecialty: React.ChangeEventHandler<HTMLInputElement> = ({
    target,
  }) => {
    setSpecialtyFilters(prev =>
      prev.includes(target.value)
        ? prev.filter(el => el !== target.value)
        : [...prev, target.value]
    )
  }

  const handleResetFilter = () => {
    setDistance(defaultState.filter.distance)
    setGender(defaultState.filter.gender)
    setAcceptingNewPatients(defaultState.filter.newPatients)
    setWithVirtualCare(defaultState.filter.withVirtualCare)
    setSpecialtyFilters(defaultState.filter.specialtyFilters)
  }

  const handleApplyFilter = useCallback(() => {
    const newFilterOptions = {
      ...initialValues,
      distance,
      gender,
      newPatients: acceptingNewPatients,
      withVirtualCare,
      specialtyFilters,
    }

    onSubmit(newFilterOptions)
    onRequestClose()
  }, [
    distance,
    gender,
    acceptingNewPatients,
    initialValues,
    withVirtualCare,
    onSubmit,
    onRequestClose,
    specialtyFilters,
  ])

  return (
    <Container className="filter-modal--card">
      <div className="card-content">
        <Header className="level is-mobile">
          <div className="level-item">
            <Title className="subtitle has-text-weight-semibold">Filters</Title>
          </div>

          <ResetButtonContainer className="level-item is-narrow">
            <GenericButton
              type="button"
              className="filter-modal--nav-btn button is-white has-text-weight-semibold"
              onClick={handleResetFilter}
            >
              Reset
            </GenericButton>
          </ResetButtonContainer>
          <div className="level-item is-narrow">
            <GenericButton
              type="button"
              className="filter-modal--nav-btn button is-white has-text-weight-semibold"
              onClick={onRequestClose}
            >
              Cancel
            </GenericButton>
          </div>
          <ApplyFilterButton
            className="level-item button is-narrow filter-modal--submit"
            onClick={handleApplyFilter}
            buttonType="link"
          >
            Apply filters
          </ApplyFilterButton>
        </Header>
        <FilterContent
          hasSpecialties={
            shouldShowSpecialtyFilters && (specialtyOptions?.length ?? 0) > 0
          }
          className="filter-modal--content"
        >
          <DistanceSelectorContainer className="filter-modal--distance-container">
            <label htmlFor="distance">
              Distance
              {isSliceTheme && <MaxMiles>100</MaxMiles>}
            </label>
            <div>
              <Slider
                type="range"
                min="1"
                max="100"
                value={distance}
                id="distance"
                onChange={handleDistanceChange}
              />
            </div>
            <p>
              {distance} Mile{distance !== 1 && 's'}
            </p>
          </DistanceSelectorContainer>

          {displayType !== 'facility' && (
            <GenderSelectorContainer className="filter-modal--gender-container">
              <label htmlFor="gender">Gender</label>
              <input
                id="gender"
                type="radio"
                value={gender}
                aria-label={gender === 'unknown' ? 'unknown' : gender}
                aria-hidden
              />
              <div className="gender-container--filter">
                <GenderButton
                  className={gender === 'unknown' ? 'active' : ''}
                  onClick={onClickAnyGender}
                  slim
                >
                  Any Gender
                </GenderButton>
                <GenderButton
                  className={gender === 'f' ? 'active' : ''}
                  slim
                  onClick={onClickFemale}
                >
                  Female
                </GenderButton>
                <GenderButton
                  className={gender === 'm' ? 'active' : ''}
                  slim
                  onClick={onClickMale}
                >
                  Male
                </GenderButton>
              </div>
            </GenderSelectorContainer>
          )}

          {displayType !== 'facility' && (
            <CheckboxContainer>
              <Checkbox
                label="Accepting new patients"
                checked={acceptingNewPatients}
                onChange={toggleAcceptingNewPatients}
              />
              {shouldShowVirtualCareAvailable && (
                <Checkbox
                  label="Virtual care available"
                  checked={withVirtualCare}
                  onChange={toggleWithVirtualCare}
                />
              )}
            </CheckboxContainer>
          )}

          {shouldShowSpecialtyFilters && specialtyOptions.length > 0 && (
            <SpecialtyContainer
              flow={
                specialtyOptions.length > 10 || isMobileView ? 'row' : 'column'
              }
              numColumns={specialtyOptions.length < 5 ? 1 : 2}
              numItems={specialtyOptions.length}
              isSafari={isSafari}
            >
              <label htmlFor="gender">
                {displayType !== 'facility' ? 'Specialty' : 'Facility type'}
              </label>
              <div className="checkbox-container">
                {specialtyOptions
                  .sort(({ name: name1 }, { name: name2 }) =>
                    name1.localeCompare(name2)
                  )
                  .map(({ name, code }) => (
                    <Checkbox
                      key={name}
                      name="Specialty filters"
                      value={code}
                      label={name}
                      onChange={onChangeSpecialty}
                      checked={specialtyFilters.includes(code)}
                    />
                  ))}
              </div>
            </SpecialtyContainer>
          )}
        </FilterContent>
      </div>
    </Container>
  )
}

export default EnhancedFilter
